import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import defaultCalendarOptions, {
  calendarEventDateTime,
  calendarEventTimeFormat,
  calendarTimeZone,
  calendarZonedDateParts,
  currentScrollTime,
  renderCalendarCentered
} from "@appComponents/helpers/full-calendar-default-config"
import FlashMessage from "src/shared/components/flash-messages"
import { loadFullCalendar } from "@appComponents/helpers/load_full_calendar"

const APPOINTMENTS_ENDPOINT = "/api/v1/appointments"
const AVAILABILITIES_ENDPOINT = "/api/v1/availabilities"
const PATIENTS_ENDPOINT = "/api/v1/profiles/patients"
const DEFAULT_EVENT_COLOR = "#3788d8"
const COMPANY_EVENT_COLOR = "#8DC838"
const UNAVAILABLE_EVENT_COLOR = "#888888"

// Set once FullCalendar and Moment are loaded (connect).
let Calendar, timeZonePlugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, frLocale, moment

export default class extends Controller {
  static targets = [
    "calendar",
    "legend",
    "appointmentModalTitle",
    "appointmentErrors",
    "appointmentType",
    "typeNotEditableHint",
    "homeCareField",
    "homeCareAddressAutocomplete",
    "changeHomeCareAddressWrapper",
    "officeField",
    "officeId",
    "appointmentDate",
    "startTime",
    "endTimeSeparator",
    "endTimeField",
    "endTime",
    "durationHint",
    "patientSection",
    "patientSearchWrapper",
    "patientSelect",
    "patientDisplayWrapper",
    "patientFirstName",
    "patientLastName",
    "patientEmail",
    "patientPhone",
    "patientProfileLink",
    "patientProfileCell",
    "patientProfileName",
    "patientProfileEmail",
    "patientProfilePhone",
    "cancelAppointmentButton",
    "submitAppointmentButton",
    "cancelModalTitle",
    "cancelErrors",
    "notifyPatient",
    "cancellationMotive",
    "createPatientErrors",
    "newPatientFirstName",
    "newPatientLastName",
    "newPatientEmail",
    "newPatientPhone"
  ]

  static values = {
    practitionerId: Number,
    appointmentDuration: Number,
    apiContext: String,
    appointmentModalId: String,
    cancelModalId: String,
    createPatientModalId: String,
    unavailableEventTitle: String,
    availabilityEventTitle: String,
    officeCaption: String,
    companyCaption: String,
    unavailableCaption: String,
    defaultEventColor: { type: String, default: DEFAULT_EVENT_COLOR },
    companyEventColor: { type: String, default: COMPANY_EVENT_COLOR },
    unavailableEventColor: { type: String, default: UNAVAILABLE_EVENT_COLOR },
    unavailableAppointmentTitle: String,
    editAppointmentTitle: String,
    newAppointmentTitle: String,
    cancelAppointmentTitle: String,
    createActionLabel: String,
    updateActionLabel: String,
    cancelAppointmentLabel: String,
    cancelUnavailableLabel: String,
    createdFlashMessage: String,
    updatedFlashMessage: String,
    cancelledFlashMessage: String,
    patientProfilePath: String
  }

  async connect () {
    this.appointments = []
    this.availabilities = []
    this.currentAppointment = null
    this.currentPatient = null
    this.editing = false
    this.pendingPatientSearchQuery = ""

    const connection = (this.connection = Symbol("connection"))
    const calendar = await loadFullCalendar()
    if (this.connection !== connection) return // disconnected (or reconnected) while loading
    ;({ Calendar, timeZonePlugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, frLocale, moment } = calendar)

    this.initializeCalendar()
  }

  disconnect () {
    this.connection = null
    this.calendar?.destroy()
  }

  initializeCalendar () {
    this.calendar = new Calendar(this.calendarTarget, {
      ...defaultCalendarOptions,
      plugins: [timeZonePlugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      locale: frLocale,
      timeZone: calendarTimeZone(),
      eventTimeFormat: calendarEventTimeFormat,
      initialDate: new URLSearchParams(window.location.search).get("date") || undefined,
      scrollTime: currentScrollTime(),
      editable: false,
      selectable: true,
      selectOverlap: (event) => event.display === "background",
      select: (info) => this.openNewAppointment(info),
      eventClick: (info) => this.openExistingAppointment(info),
      events: (fetchInfo, successCallback, failureCallback) => {
        this.fetchEvents(fetchInfo.startStr, fetchInfo.endStr)
          .then((events) => successCallback(events))
          .catch((error) => failureCallback(error))
      }
    })

    renderCalendarCentered(this.calendar, this.calendarTarget)
  }

  async fetchEvents (startAt, endAt) {
    const commonParams = {
      start_at: startAt,
      end_at: endAt,
      practitioner_id: this.practitionerIdValue
    }

    const [appointmentsResponse, availabilitiesResponse] = await Promise.all([
      fetch(`${APPOINTMENTS_ENDPOINT}?${new URLSearchParams({
        ...commonParams,
        as: this.apiContextValue,
        by_aasm_state: "confirmed"
      })}`, { headers: { Accept: "application/json" }, credentials: "same-origin" }),
      fetch(`${AVAILABILITIES_ENDPOINT}?${new URLSearchParams(commonParams)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
    ])

    const appointmentsData = await appointmentsResponse.json()
    const availabilitiesData = await availabilitiesResponse.json()

    this.appointments = appointmentsData.data || []
    this.availabilities = availabilitiesData.data || []

    this.renderLegend()
    return this.buildEvents()
  }

  buildEvents () {
    const events = []

    this.appointments.forEach((appointment) => {
      const attributes = appointment.attributes
      const isUnavailable = attributes.appointment_type === "unavailable"
      const isPast = moment(attributes.start_at).isBefore(moment())
      const color = this.appointmentColor(attributes)

      events.push({
        id: appointment.id,
        title: isUnavailable ? this.unavailableEventTitleValue : attributes.patient_full_name,
        start: calendarEventDateTime(attributes.start_at),
        end: calendarEventDateTime(attributes.end_at),
        extendedProps: { appointment, isPast, provenanceType: attributes.provenance_type },
        backgroundColor: color,
        borderColor: color,
        classNames: [
          isPast ? "fc-event--past" : "",
          attributes.provenance_type === "reflexosteo" ? "fc-event--reflexosteo" : ""
        ].filter(Boolean)
      })
    })

    this.availabilities.forEach((availability) => {
      const attributes = availability.attributes
      const isPast = moment(attributes.start_at).isBefore(moment())
      const color = this.resolvedEventColor(attributes.color)

      events.push({
        id: `availability-${availability.id}`,
        title: attributes.patient_full_name || this.availabilityEventTitleValue,
        start: calendarEventDateTime(attributes.start_at),
        end: calendarEventDateTime(attributes.end_at),
        extendedProps: { availability, isPast },
        backgroundColor: "transparent",
        borderColor: color,
        classNames: ["fc-event--availability", isPast ? "fc-event--past" : ""].filter(Boolean)
      })
    })

    return events
  }

  renderLegend () {
    const captions = []

    const addCaption = (label, color) => {
      if (!label || captions.some((caption) => caption.label === label)) return

      captions.push({ label, color: this.resolvedEventColor(color) })
    }

    this.availabilities.forEach((availability) => {
      const attributes = availability.attributes
      if (!attributes.office_name) return

      addCaption(
        this.interpolate(this.officeCaptionValue, { office_name: attributes.office_name }),
        attributes.color
      )
    })

    this.appointments.forEach((appointment) => {
      const attributes = appointment.attributes
      if (attributes.appointment_type === "company") return
      if (!attributes.office_name) return

      addCaption(
        this.interpolate(this.officeCaptionValue, { office_name: attributes.office_name }),
        attributes.color
      )
    })

    captions.sort((left, right) => left.label.localeCompare(right.label))

    if (this.hasCompanyCaptionValue) {
      captions.push({
        label: this.companyCaptionValue,
        color: this.companyEventColorValue
      })
    }

    if (this.hasUnavailableCaptionValue) {
      captions.push({
        label: this.unavailableCaptionValue,
        color: this.unavailableEventColorValue
      })
    }

    this.legendTarget.innerHTML = captions.map((caption) => `
      <div class="d-flex align-items-center mb-1">
        <span class="color-picker__choice" style="background-color: ${caption.color}"></span>
        <span class="ps-1">${caption.label}</span>
      </div>
    `).join("")
  }

  appointmentColor (attributes) {
    if (attributes.appointment_type === "unavailable") return this.unavailableEventColorValue
    if (attributes.appointment_type === "company") return this.companyEventColorValue

    return this.resolvedEventColor(attributes.color)
  }

  resolvedEventColor (color) {
    return color || this.defaultEventColorValue
  }

  openNewAppointment (selectionInfo) {
    this.editing = false
    this.currentPatient = null
    this.currentAppointment = this.newAppointmentPayload()
    const start = calendarZonedDateParts(selectionInfo.startStr || selectionInfo.start)

    this.currentAppointment.attributes.date = start.date
    this.currentAppointment.attributes.start_time = start.time
    this.currentAppointment.attributes.end_time = moment(`${start.isoDate}T${start.time}`, "YYYY-MM-DDTHH:mm")
      .add(this.appointmentDurationValue, "minutes")
      .format("HH:mm")

    this.populateAppointmentForm()
    this.showAppointmentModal()
    this.calendar.unselect()
  }

  async openExistingAppointment (eventInfo) {
    const appointment = eventInfo.event.extendedProps.appointment
    if (!appointment) return

    const response = await fetch(`${APPOINTMENTS_ENDPOINT}?by_id=${appointment.id}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    const data = await response.json()

    this.editing = true
    this.currentAppointment = data.data[0]
    await this.loadPatient()
    this.populateAppointmentForm()
    this.showAppointmentModal()
  }

  async loadPatient () {
    const patientId = this.currentAppointment?.attributes?.patient_id
    if (!patientId) {
      this.currentPatient = null
      return
    }

    const response = await fetch(`${PATIENTS_ENDPOINT}?by_id=${patientId}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    const data = await response.json()
    this.currentPatient = data.data?.[0] || null
  }

  populateAppointmentForm () {
    const attributes = this.currentAppointment.attributes

    this.clearErrors(this.appointmentErrorsTarget)
    this.appointmentTypeTarget.value = attributes.appointment_type || "office"
    this.appointmentTypeTarget.disabled = this.editing
    this.typeNotEditableHintTarget.classList.toggle("d-none", !this.editing)

    const companyOption = this.appointmentTypeTarget.querySelector('option[value="company"]')
    if (companyOption) companyOption.hidden = attributes.appointment_type !== "company"

    if (attributes.office_id) {
      this.officeIdTarget.value = attributes.office_id
    } else if (this.officeIdTarget.options.length > 1) {
      this.officeIdTarget.selectedIndex = 1
    }

    this.appointmentDateTarget.value = attributes.date || ""
    this.startTimeTarget.value = attributes.start_time || ""
    this.endTimeTarget.value = attributes.end_time || ""
    this.startTimeTarget.disabled = this.editing && attributes.can_update === false

    this.populateHomeCareAddress(attributes)
    this.updateAppointmentTitle()
    this.toggleAppointmentFields()
    this.updatePatientSection()
    this.updateAppointmentActions()
  }

  populateHomeCareAddress (attributes) {
    const address = attributes.home_care_address?.attributes || attributes.home_care_address_attributes || {}

    this.setAddressFieldValue("input", attributes.address_display_name || address.display_name || "")
    this.setAddressFieldValue("longitude", address.longitude || "")
    this.setAddressFieldValue("latitude", address.latitude || "")
    this.setAddressFieldValue("city", address.city || "")
    this.setAddressFieldValue("country", address.country || "")
    this.setAddressFieldValue("postalCode", address.postal_code || "")
    this.setAddressFieldValue("streetNumber", address.street_number || "")
    this.setAddressFieldValue("streetName", address.street_name || "")
    this.setAddressFieldValue("displayName", address.display_name || "")
    this.setAddressFieldValue("destroy", address.destroy || "")
    this.changeHomeCareAddressWrapperTarget.classList.toggle(
      "d-none",
      !attributes.address_display_name
    )
  }

  setAddressFieldValue (name, value) {
    const field = this.addressField(name)
    if (field) field.value = value
  }

  addressField (name) {
    const root = this.homeCareAddressAutocompleteTarget?.querySelector("[data-controller~='address-autocomplete']")
    return root?.querySelector('[data-address-autocomplete-target="' + name + '"]')
  }

  changeHomeCareAddress () {
    this.setAddressFieldValue("input", "")
    this.setAddressFieldValue("displayName", "")
    this.changeHomeCareAddressWrapperTarget.classList.add("d-none")
  }

  toggleAppointmentFields () {
    const appointmentType = this.appointmentTypeTarget.value

    this.officeFieldTarget.classList.toggle("d-none", appointmentType !== "office")
    this.homeCareFieldTarget.classList.toggle("d-none", appointmentType !== "home_care")
    this.endTimeFieldTarget.classList.toggle("d-none", appointmentType !== "unavailable")
    this.endTimeSeparatorTarget.classList.toggle("d-none", appointmentType !== "unavailable")
    this.durationHintTarget.classList.toggle("d-none", appointmentType === "unavailable")
    this.patientSectionTarget.classList.toggle(
      "d-none",
      this.editing || appointmentType === "unavailable" || appointmentType === "company"
    )
  }

  updateEndTimeFromDuration () {
    if (this.appointmentTypeTarget.value === "unavailable") return

    const start = moment(this.startTimeTarget.value, "HH:mm")
    if (!start.isValid()) return

    this.endTimeTarget.value = start.add(this.appointmentDurationValue, "minutes").format("HH:mm")
  }

  updatePatientSection () {
    if (this.currentPatient?.id) {
      this.patientSearchWrapperTarget.classList.add("d-none")
      this.patientDisplayWrapperTarget.classList.remove("d-none")
      this.patientFirstNameTarget.value = this.currentPatient.attributes.first_name || ""
      this.patientLastNameTarget.value = this.currentPatient.attributes.last_name || ""
      this.patientEmailTarget.value = this.currentPatient.attributes.contact_email || ""
      this.patientPhoneTarget.value = this.currentPatient.attributes.phone || ""
      if (this.hasPatientProfileLinkTarget && this.patientProfilePathValue) {
        this.patientProfileLinkTarget.href = this.interpolate(this.patientProfilePathValue, {
          patient_id: this.currentPatient.id
        })
        this.patientProfileLinkTarget.classList.remove("d-none")
      }
    } else {
      this.patientSearchWrapperTarget.classList.remove("d-none")
      this.patientDisplayWrapperTarget.classList.add("d-none")
      this.resetPatientSelect()
    }

    this.updatePatientProfileCell()
  }

  updatePatientProfileCell () {
    if (!this.hasPatientProfileCellTarget) return

    const appointmentType = this.appointmentTypeTarget?.value ||
      this.currentAppointment?.attributes?.appointment_type
    const appointmentAttributes = this.currentAppointment?.attributes || {}
    const patientAttributes = this.currentPatient?.attributes || {}
    const fullName = this.patientDisplayName(patientAttributes, appointmentAttributes)
    const email = patientAttributes.contact_email ||
      patientAttributes.email ||
      appointmentAttributes.patient_email ||
      ""
    const phone = patientAttributes.phone || appointmentAttributes.patient_phone || ""
    const show = this.editing &&
      appointmentType !== "unavailable" &&
      Boolean(fullName || this.currentPatient?.id || appointmentAttributes.patient_id)

    this.patientProfileCellTarget.classList.toggle("d-none", !show)
    this.patientProfileCellTarget.classList.toggle("practitioner-agenda__patient-profile--visible", show)
    if (!show) return

    this.patientProfileNameTarget.textContent = fullName
    this.patientProfileEmailTarget.textContent = email
    this.patientProfilePhoneTarget.textContent = phone
    this.patientProfileEmailTarget.classList.toggle("d-none", !email)
    this.patientProfilePhoneTarget.classList.toggle("d-none", !phone)
  }

  patientDisplayName (patientAttributes, appointmentAttributes) {
    const firstName = (patientAttributes.first_name || "").trim()
    const lastName = (patientAttributes.last_name || "").trim()

    return [this.capitalizeName(firstName), lastName.toUpperCase()].filter(Boolean).join(" ") ||
      patientAttributes.full_name ||
      appointmentAttributes.patient_full_name ||
      ""
  }

  capitalizeName (value) {
    if (!value) return ""

    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  updateAppointmentActions () {
    const attributes = this.currentAppointment.attributes
    const canSubmit = attributes.can_update !== false || !this.editing
    const canCancel = this.editing &&
      attributes.aasm_state !== "cancelled" &&
      attributes.can_cancel !== false

    this.submitAppointmentButtonTarget.classList.toggle("d-none", !canSubmit)
    this.submitAppointmentButtonTarget.textContent = this.editing
      ? this.updateActionLabelValue
      : this.createActionLabelValue
    this.cancelAppointmentButtonTarget.classList.toggle("d-none", !canCancel)
    this.cancelAppointmentButtonTarget.textContent = attributes.appointment_type === "unavailable"
      ? this.cancelUnavailableLabelValue
      : this.cancelAppointmentLabelValue
  }

  updateAppointmentTitle () {
    const attributes = this.currentAppointment.attributes
    const date = attributes.date
    const patientName = attributes.patient_full_name || this.currentPatient?.attributes?.full_name
    const phone = this.currentPatient?.attributes?.phone
    const phoneSuffix = phone ? ` (${phone})` : ""

    if (attributes.appointment_type === "unavailable") {
      this.appointmentModalTitleTarget.textContent = this.interpolate(
        this.unavailableAppointmentTitleValue,
        { date }
      )
    } else if (this.editing) {
      this.appointmentModalTitleTarget.textContent = this.interpolate(
        this.editAppointmentTitleValue,
        { date, patient_name: patientName, phone: phoneSuffix }
      )
    } else {
      this.appointmentModalTitleTarget.textContent = this.interpolate(
        this.newAppointmentTitleValue,
        { date }
      )
    }

    if (this.editing) {
      this.cancelModalTitleTarget.textContent = this.interpolate(
        this.cancelAppointmentTitleValue,
        { date }
      )
    }
  }

  selectPatient (event) {
    const tomSelect = this.patientSelectTarget.tomselect
    const value = event?.target?.value || tomSelect?.getValue()

    if (!value) {
      this.currentPatient = null
      this.updatePatientSection()
      return
    }

    const option = tomSelect.options[value]
    this.currentPatient = option?.data || null
    this.currentAppointment.attributes.patient_id = this.currentPatient?.id || ""
    this.updatePatientSection()
    this.updateAppointmentTitle()
  }

  changePatient () {
    this.currentPatient = null
    this.currentAppointment.attributes.patient_id = ""
    this.updatePatientSection()
    this.updateAppointmentTitle()
  }

  openCreatePatientModal () {
    const tomSelect = this.patientSelectTarget.tomselect
    this.pendingPatientSearchQuery = tomSelect?.control?.value || ""
    this.resetCreatePatientForm()
    this.hideModal(this.appointmentModalIdValue)
    this.showModal(this.createPatientModalIdValue)
  }

  backToAppointmentModalFromCreatePatient () {
    this.hideModal(this.createPatientModalIdValue)
    this.showModal(this.appointmentModalIdValue)
  }

  backToAppointmentModal () {
    this.hideModal(this.cancelModalIdValue)
    this.showModal(this.appointmentModalIdValue)
  }

  async submitAppointment (event) {
    event?.preventDefault()
    this.syncAppointmentAttributes()

    const method = this.editing ? "PUT" : "POST"
    const url = this.editing ? `${APPOINTMENTS_ENDPOINT}/${this.currentAppointment.id}` : APPOINTMENTS_ENDPOINT

    const response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      credentials: "same-origin",
      body: JSON.stringify({
        as: this.apiContextValue,
        appointment: this.currentAppointment.attributes
      })
    })

    if (response.ok) {
      this.hideModal(this.appointmentModalIdValue)
      this.calendar.refetchEvents()
      this.showFlash(this.editing ? this.updatedFlashMessageValue : this.createdFlashMessageValue, "success")
      return
    }

    const data = await response.json()
    this.currentAppointment = data.data
    this.displayErrors(this.appointmentErrorsTarget, this.currentAppointment.attributes.errors || [])
  }

  openCancelModal () {
    if (this.currentAppointment.attributes.appointment_type === "unavailable") {
      this.submitCancellation(true)
      return
    }

    this.hideModal(this.appointmentModalIdValue)
    this.clearErrors(this.cancelErrorsTarget)
    this.notifyPatientTarget.checked = false
    this.cancellationMotiveTarget.value = ""
    this.showModal(this.cancelModalIdValue)
  }

  async submitCancellation (skipCancelModal = false) {
    const withoutModal = skipCancelModal === true
    const appointment = {}

    if (withoutModal) {
      appointment.notify_patient = false
    } else {
      appointment.notify_patient = this.notifyPatientTarget.checked
      appointment.cancellation_motive = this.cancellationMotiveTarget.value
    }

    const response = await fetch(`${APPOINTMENTS_ENDPOINT}/${this.currentAppointment.id}`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      credentials: "same-origin",
      body: JSON.stringify({
        appointment,
        cancel: true
      })
    })

    if (response.ok) {
      this.hideModal(this.appointmentModalIdValue)
      this.hideModal(this.cancelModalIdValue)
      this.calendar.refetchEvents()
      this.showFlash(this.cancelledFlashMessageValue, "success")
      return
    }

    const data = await response.json()
    this.currentAppointment = data.data
    const target = withoutModal ? this.appointmentErrorsTarget : this.cancelErrorsTarget
    this.displayErrors(target, this.currentAppointment.attributes.errors || [])
  }

  async submitPatient () {
    this.clearErrors(this.createPatientErrorsTarget)

    const patientAttributes = {
      first_name: this.newPatientFirstNameTarget.value,
      last_name: this.newPatientLastNameTarget.value,
      contact_email: this.newPatientEmailTarget.value,
      phone: this.newPatientPhoneTarget.value
    }

    const response = await fetch(PATIENTS_ENDPOINT, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      credentials: "same-origin",
      body: JSON.stringify({ patient: patientAttributes })
    })

    const data = await response.json()

    if (response.ok) {
      this.currentPatient = data.data
      this.currentAppointment.attributes.patient_id = this.currentPatient.id
      this.hideModal(this.createPatientModalIdValue)
      this.populateAppointmentForm()
      this.showModal(this.appointmentModalIdValue)
      return
    }

    this.displayErrors(this.createPatientErrorsTarget, data.data?.attributes?.errors || [])
  }

  syncAppointmentAttributes () {
    const appointmentType = this.appointmentTypeTarget.value

    this.currentAppointment.attributes.appointment_type = appointmentType
    this.currentAppointment.attributes.office_id = this.officeIdTarget.value
    this.currentAppointment.attributes.date = this.appointmentDateTarget.value
    this.currentAppointment.attributes.start_time = this.startTimeTarget.value
    this.currentAppointment.attributes.end_time = this.endTimeTarget.value
    this.currentAppointment.attributes.patient_id = this.currentPatient?.id || ""
    this.currentAppointment.attributes.practitioner_id = this.practitionerIdValue

    if (appointmentType === "home_care") {
      this.currentAppointment.attributes.home_care_address_attributes = {
        longitude: this.addressField("longitude")?.value,
        latitude: this.addressField("latitude")?.value,
        city: this.addressField("city")?.value,
        country: this.addressField("country")?.value,
        postal_code: this.addressField("postalCode")?.value,
        street_number: this.addressField("streetNumber")?.value,
        street_name: this.addressField("streetName")?.value,
        display_name: this.addressField("displayName")?.value,
        destroy: this.addressField("destroy")?.value
      }
    }
  }

  resetCreatePatientForm () {
    const query = this.pendingPatientSearchQuery.trim()
    const parts = query.split(/\s+/)

    this.newPatientFirstNameTarget.value = parts.length > 1 ? parts.slice(0, -1).join(" ") : query
    this.newPatientLastNameTarget.value = parts.length > 1 ? parts.at(-1) : ""
    this.newPatientEmailTarget.value = query.includes("@") ? query : ""
    this.newPatientPhoneTarget.value = ""
    this.clearErrors(this.createPatientErrorsTarget)
  }

  resetPatientSelect () {
    const tomSelect = this.patientSelectTarget.tomselect
    if (!tomSelect) return

    tomSelect.clear()
    tomSelect.clearOptions()
  }

  newAppointmentPayload () {
    return {
      id: null,
      attributes: {
        appointment_type: "office",
        date: "",
        start_time: "",
        end_time: "",
        patient_id: "",
        office_id: "",
        patient_full_name: "",
        cancellation_motive: "",
        for_other_person: false,
        errors: [],
        can_update: true,
        can_cancel: true,
        home_care_address: { attributes: {} }
      }
    }
  }

  showAppointmentModal () {
    this.showModal(this.appointmentModalIdValue)
  }

  showModal (modalId) {
    const element = document.getElementById(modalId)
    if (element) Modal.getOrCreateInstance(element).show()
  }

  hideModal (modalId) {
    const element = document.getElementById(modalId)
    const modal = element ? Modal.getInstance(element) : null
    if (modal) modal.hide()
  }

  showFlash (message, kind) {
    new FlashMessage(message, kind)
  }

  clearErrors (target) {
    target.innerHTML = ""
  }

  displayErrors (target, errors) {
    if (!errors?.length) {
      this.clearErrors(target)
      return
    }

    target.innerHTML = `
      <div class="alert alert-danger">
        <ul class="mb-0">
          ${errors.map((error) => `<li>${error}</li>`).join("")}
        </ul>
      </div>
    `
  }

  interpolate (template, values) {
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replace(new RegExp(`%{${key}}`, "g"), value ?? ""),
      template
    )
  }

  get csrfToken () {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
